/* eslint-disable @typescript-eslint/restrict-template-expressions */

import { getThemeCSSVars } from 'themes/helpers';
import { FontSet, GGTheme } from 'themes/types';

import { useAppContext } from 'hooks/context/useAppContext';
import { usePreferences } from 'hooks/preferences/usePreferences';

import { defaultGradient, gradientToCssRule } from '../utils/gradientUtils';

type Props = {
  theme: GGTheme;
};

const GlobalStyles = ({ theme }: Props) => {
  const { publicUser } = useAppContext();
  const { brandColorPrimaryOverride } = usePreferences();

  const organizationGradient = publicUser?.organization_gradient;
  const brandPrimaryColor =
    theme.accentColorOverride ||
    brandColorPrimaryOverride ||
    organizationGradient ||
    defaultGradient;

  // hack hack: using a default when no theme
  const fonts: FontSet = theme?.fonts
    ? theme.fonts
    : {
        token: 'default',
        name: 'Default',
        headings: [],
        body: [],
      };

  const colors = (
    theme
      ? {
          ...theme.palette,
          accentPrimary: brandPrimaryColor,
        }
      : {
          accentPrimary: brandPrimaryColor,
          accentSecondary: '#5A5E6D',
          accentTertiary: '#727587',
          textPrimary: '#FFFFFF',
          textSecondary: '#000000',
          textTertiary: '#9295A5',
          textActive: '#FFFFFF',
          bgPrimary: '#2C2E32',
          bgSecondary: '#1B1D21',
          bgTertiary: '#21242A',
          bgActive: '#21242A',
        }
  ) as GGTheme['palette'];

  if (typeof colors.accentPrimary === 'object') {
    colors.accentGradient = colors.accentPrimary;
    colors.accentPrimary = gradientToCssRule(colors.accentPrimary);
  }

  return (
    <>
      {/* eslint-disable react/no-unknown-property */}
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <style jsx global>{`
        ${fonts?.body
          ? fonts.body.map(({ weight, url }) => {
              return `@font-face {
          font-family: BaseFont;
          font-weight: ${weight};
          src: url('${url}');
        }\n`;
            })
          : ''}

        ${fonts.headings
          ? fonts.headings.map(({ weight, url }) => {
              return `@font-face {
          font-family: HeadingFont;
          font-weight: ${weight};
          src: url('${url}');
        }\n`;
            })
          : ''}

        :root {
          --palette-neutral-grey: #c4bcb9;

          ${getThemeCSSVars(colors)}

          --focus-box-shadow: 0 0 0 1px var(--accent-tertiary);
          --focus-box-shadow-thick: 0 0 0 3px var(--accent-tertiary);

          --max-content-width: 1497px;
          --min-button-width-desktop: 13rem;
          --min-button-width-mobile: 9rem;

          --font-heading-ratio: ${fonts.headings[0]?.ratio || 1};
          --font-body-ratio: ${fonts.body[0]?.ratio || 1};
        }
        .brand-primary-color {
          background: var(--accent-primary);
        }
        html {
          background: var(--bg-primary);
        }

        /* class for visually hidden, but accessible elements */
        .hidden {
          clip: rect(0 0 0 0);
          clip-path: inset(50%);
          height: 1px;
          overflow: hidden;
          position: absolute;
          white-space: nowrap;
          width: 1px;
        }
      `}</style>
      {/* eslint-enable react/no-unknown-property */}
      <svg width="0" height="0" style={{ display: 'block' }}>
        <defs>
          <linearGradient
            id="brand-primary-color"
            x1="0"
            y1="0"
            x2="100%"
            y2="0"
          >
            {brandPrimaryColor.colors.map((color, index) => {
              const location = brandPrimaryColor.locations[index];
              const stopOpacity =
                index === brandPrimaryColor.colors.length - 1 ? 0 : undefined;

              return (
                <stop
                  key={`${location}-${color}`}
                  offset={location}
                  stopColor={color}
                  stopOpacity={stopOpacity}
                />
              );
            })}
          </linearGradient>
        </defs>
      </svg>
    </>
  );
};

export { GlobalStyles };
